import { Checkbox, CheckboxProps } from "@ui/elements/checkbox";
import { Combobox, ComboboxProps } from "@ui/elements/combobox";
import { Input, InputProps } from "@ui/elements/input";
import NativeSelect from "@ui/elements/native-select";
import { Radio, RadioProps } from "@ui/elements/radio";
import Select, { SelectOption, SelectProps } from "@ui/elements/select";
import { Textarea } from "@ui/elements/textarea";
import { Toggle, ToggleProps } from "@ui/elements/toggle";
import { find, isArray, map } from "lodash";
import { forwardRef } from "react";
import { Field, FieldProps, FieldRenderProps } from "react-final-form";
import type { Props } from "react-select";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { StaticSelect } from "@/components/ui/select-new";

type TextFieldProps = InputProps &
  FieldProps<string, FieldRenderProps<string, HTMLElement, string>>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({ name, ...props }, ref) => {
  return (
    <Field<string>
      name={name}
      {...props}
      render={(fieldProps) => {
        const { meta, input, ...rest } = fieldProps;
        return (
          <Input
            {...meta}
            {...input}
            {...rest}
            ref={ref}
            error={fieldProps.meta.touched ? fieldProps.meta.error : undefined}
          />
        );
      }}
    />
  );
});

TextField.displayName = "TextField";

type TextareaFieldProps = InputProps &
  FieldProps<string, FieldRenderProps<string, HTMLElement, string>>;

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  ({ name, ...props }, ref) => {
    return (
      <Field<string>
        name={name}
        {...props}
        render={(fieldProps) => {
          const { meta, input, ...rest } = fieldProps;
          return (
            <Textarea
              {...meta}
              {...input}
              {...rest}
              ref={ref}
              error={fieldProps.meta.touched ? fieldProps.meta.error : undefined}
            />
          );
        }}
      />
    );
  },
);

TextareaField.displayName = "TextareaField";

type RadioFieldProps = RadioProps &
  FieldProps<string, FieldRenderProps<string, HTMLElement, string>>;

export const RadioField = forwardRef<HTMLInputElement, RadioFieldProps>(
  ({ name, ...props }, ref) => {
    return (
      <Field<string>
        name={name}
        {...props}
        type="radio"
        value={props.value}
        render={(fieldProps) => {
          const { meta, input, ...rest } = fieldProps;
          return (
            <Radio
              {...meta}
              {...input}
              {...rest}
              ref={ref}
              error={fieldProps.meta.touched ? fieldProps.meta.error : undefined}
            />
          );
        }}
      />
    );
  },
);

RadioField.displayName = "RadioField";

type CheckboxFieldProps = CheckboxProps &
  FieldProps<string, FieldRenderProps<string, HTMLElement, string>>;

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  ({ name, ...props }, ref) => {
    return (
      <Field<string>
        name={name}
        {...props}
        type="checkbox"
        value={props.value}
        render={(fieldProps) => {
          const { meta, input, ...rest } = fieldProps;
          return (
            <Checkbox
              {...meta}
              {...input}
              {...rest}
              ref={ref}
              error={fieldProps.meta.touched ? fieldProps.meta.error : undefined}
            />
          );
        }}
      />
    );
  },
);

CheckboxField.displayName = "CheckboxField";

type _SelectFieldProps<T extends SelectOption> = Omit<SelectProps<T>, "value" | "onChange"> &
  FieldProps<string, FieldRenderProps<string, HTMLElement, string>>;

export const _SelectField = <T extends SelectOption>(props: _SelectFieldProps<T>) => {
  return (
    <Field<string>
      {...props}
      render={(fieldProps: any) => {
        const { meta, input, ...rest } = fieldProps;
        return (
          <Select
            {...meta}
            {...input}
            {...rest}
            error={fieldProps.meta.touched ? fieldProps.meta.error : undefined}
          />
        );
      }}
    />
  );
};

_SelectField.displayName = "_SelectField";

type NativeSelectFieldProps<T extends SelectOption> = Omit<SelectProps<T>, "value" | "onChange"> &
  FieldProps<string, FieldRenderProps<string, HTMLElement, string>>;

export const NativeSelectField = <T extends SelectOption>(props: NativeSelectFieldProps<T>) => {
  return (
    <Field<string>
      {...props}
      render={(fieldProps: any) => {
        const { meta, input, ...rest } = fieldProps;
        return (
          <NativeSelect
            {...meta}
            {...input}
            {...rest}
            error={fieldProps.meta.touched ? fieldProps.meta.error : undefined}
          />
        );
      }}
    />
  );
};

NativeSelectField.displayName = "NativeSelectField";

type ToggleFieldProps = ToggleProps &
  FieldProps<string, FieldRenderProps<string, HTMLElement, string>>;

export const ToggleField = forwardRef<HTMLInputElement, ToggleFieldProps>(
  ({ name, onChange, ...props }, _ref) => {
    return (
      <Field<string>
        {...props}
        name={name}
        type="checkbox"
        value={props.value}
        render={(fieldProps) => {
          const { meta, input, ...rest } = fieldProps;
          return (
            <Toggle
              {...meta}
              {...input}
              {...rest}
              onChange={onChange}
              error={meta.touched ? meta.error : undefined}
            />
          );
        }}
      />
    );
  },
);

ToggleField.displayName = "ToggleField";

type ComboboxFieldProps = Omit<ComboboxProps, "selectedOption" | "onSelectOption"> & {
  options: SelectOption[];
} & FieldProps<string, FieldRenderProps<string, any, string>>;

export const ComboboxField = forwardRef<any, ComboboxFieldProps>(({ name, ...props }, ref) => {
  return (
    <Field<string>
      name={name}
      {...props}
      innerRef={ref}
      render={({ input: { value, onChange }, meta: _meta, ...rest }) => (
        <Combobox
          {...rest}
          options={props.options}
          selectedOption={props.options.find((option: any) => option.id === value) || null}
          onSelectOption={(option) => onChange(option.id)}
        />
      )}
    />
  );
});

ComboboxField.displayName = "ComboboxField";

interface Option {
  label: string;
  value: string;
}

type NewSelectFieldProps<T extends Option> = Omit<Props<T>, "value" | "onChange" | "defaultValue"> &
  FieldProps<string, FieldRenderProps<string, HTMLElement, string>>;

export const SelectField = <T extends Option>(props: NewSelectFieldProps<T>) => {
  return (
    <Field<string>
      // parse returns string | string[] from (val: Option)
      parse={(val: any) => (val && isArray(val) ? map(val, (item) => item.value) : val?.value)}
      // format returns Option from (val: string | string[])
      format={(val): any =>
        val
          ? isArray(val)
            ? map(val, (item) => find(props.options, (o: any) => o.value === item))
            : find(props.options, (o: any) => o.value === val)
          : ""
      }
      {...props}
      render={({ input, meta, ...rest }) => (
        <StaticSelect
          {...meta}
          {...input}
          {...rest}
          onChange={(val) => {
            props.onChange?.();
            input.onChange(val);
          }}
          options={props.options}
          hideDropdownIndicator={props.hideDropdownIndicator}
        />
      )}
    />
  );
};

SelectField.displayName = "SelectField";

export const SelectField2 = <T extends Option>(props: NewSelectFieldProps<T>) => {
  return (
    <FormField
      {...props}
      name={`${props.name}.op`}
      render={({ field }) => {
        return (
          <FormItem>
            <FormControl>
              <StaticSelect
                {...field}
                onChange={(newValue: unknown) => {
                  const value = (newValue as { label: string; value: string }).value;
                  if (props.onChange) {
                    props.onChange();
                  } else {
                    field.onChange(value);
                  }
                }}
                options={props.options}
                hideDropdownIndicator={props.hideDropdownIndicator}
                maxMenuHeight={props.maxMenuHeight || 250}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};
